var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement, nothing } from "lit";
import { property } from "lit/decorators.js";
import { activateI18n } from "../i18n";
import "../link/link.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { reset } from "../styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import { wButtonStyles } from "./styles.js";
const variants = [
    "primary",
    "secondary",
    "negative",
    "negativeQuiet",
    "utility",
    "pill",
    "link",
    "quiet",
    "utilityQuiet",
    "overlay",
    "overlayInverted",
    "overlayQuiet",
    "overlayInvertedQuiet",
];
/**
 * Performs an action or renders a link with button styling.
 *
 * Use button variants to match action priority, risk, and context.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/button/frameworks/elements)
 */
class WarpButton extends FormControlMixin(LitElement) {
    static { this.styles = [reset, wButtonStyles]; }
    // capture the initial value using connectedCallback and #initialValue
    #initialValue;
    /**
     * We want to emulate native button behavior which fires click listeners when space is
     * pressed and released on the same element. To avoid emulating click only on keyup,
     * keep track of whether we got a keydown-event. This value must reset on blur.
     */
    #gotSpaceKeydownAndNoBlur;
    get #ariaDescription() {
        // let users override our default description
        if (this.ariaDescription) {
            return this.ariaDescription;
        }
        if (this.variant === "primary") {
            return i18n.t({
                id: "button.aria.primaryDescription",
                message: "Highlighted",
                comment: "Short (preferably a single word) description indicating to screen reader users that this button is visually highlighted",
            });
        }
        if (this.variant === "negative" || this.variant === "negativeQuiet") {
            return i18n.t({
                id: "button.aria.negativeDescription",
                message: "Attention",
                comment: "Short (preferably a single word) description indicating to screen reader users that this button tries to get the user's attention",
            });
        }
        return undefined;
    }
    updated(changedProperties) {
        if (changedProperties.has("value")) {
            this.setValue(this.value);
        }
    }
    constructor() {
        super();
        /**
         * Button type.
         * Controls whether the internal button behaves as a regular button, submits a form, or resets a form. Defaults to `button`.
         */
        this.type = "button";
        /**
         * Focuses the button when it is first rendered.
         * Applies only when the component renders as a button (as opposed to an anchor/link). Link buttons with `href` do not autofocus through this component.
         */
        this.autofocus = false;
        /**
         * Visual style of the button.
         * Defaults to `secondary`. Use the variant that matches the action priority, risk, and placement.
         */
        this.variant = "secondary";
        /**
         * Deprecated quiet visual treatment flag
         * Use `variant="quiet"` instead.
         *
         * @deprecated Use `variant="quiet"` instead
         */
        this.quiet = false;
        /**
         * Marks the button as icon-only.
         * Use this when the button has no visible text. Include accessible text in the slotted content so the internal button has a name.
         */
        this.iconOnly = false;
        /**
         * Renders the compact button size.
         * Use this in dense layouts where the default button size is too large.
         */
        this.small = false;
        /**
         * Shows the loading state.
         * Use after the user has triggered an action and the action is in progress.
         */
        this.loading = false;
        /**
         * Visually disables the button.
         * Disabled buttons are discouraged because they can hide the reason an action is unavailable.
         */
        this.disabled = false;
        /**
         * Makes the button fill its parent width.
         * Useful in narrow layouts where the button should span the available inline space.
         */
        this.fullWidth = false;
        // capture the initial value using connectedCallback and #initialValue
        this.#initialValue = undefined;
        /**
         * We want to emulate native button behavior which fires click listeners when space is
         * pressed and released on the same element. To avoid emulating click only on keyup,
         * keep track of whether we got a keydown-event. This value must reset on blur.
         */
        this.#gotSpaceKeydownAndNoBlur = false;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
        this.ariaValueTextLoading = i18n.t({
            id: "button.aria.loading",
            message: "Loading...",
            comment: "Screen reader message for buttons that are loading",
        });
    }
    connectedCallback() {
        super.connectedCallback();
        const effectiveVariant = this.variant || "secondary";
        if (!variants.includes(effectiveVariant)) {
            throw new Error(`Invalid "variant" attribute. Set its value to one of the following:\n${variants.join(", ")}.`);
        }
        if (!this.href && !this.hasAttribute("role")) {
            // Using this.internals for role breaks Playwright locators.
            // Set in connectedCallback to avoid a role attribute when we render w-link.
            this.setAttribute("role", "button");
        }
        if (!this.href && !this.hasAttribute("tabindex")) {
            this.setAttribute("tabindex", "0");
        }
        this.internals.ariaDescription = this.#ariaDescription;
        this.addEventListener("click", this._handleButtonClick);
        this.addEventListener("blur", () => {
            this.#gotSpaceKeydownAndNoBlur = false;
        });
        this.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                this.click(); // call this.click so user-provided click handlers get triggered
            }
            if (e.key === " ") {
                // Stop Space from scrolling.
                e.preventDefault();
                // Mark the button as active while space is held.
                // We don't get access to the :active pseudo class when interacting with a keyboard unfortunately.
                this.dataset.active = "true";
                this.#gotSpaceKeydownAndNoBlur = true;
            }
        });
        this.addEventListener("keyup", (e) => {
            if (e.key === " ") {
                delete this.dataset.active;
                if (this.#gotSpaceKeydownAndNoBlur) {
                    this.click(); // call this.click so user-provided click handlers get triggered
                }
                this.#gotSpaceKeydownAndNoBlur = false;
            }
        });
        this.#initialValue = this.value;
    }
    /**
     * Traverse up the shadow roots looking for the ID to support use inside other Lit components.
     */
    closestWithId(id) {
        let root = this.shadowRoot;
        let el;
        try {
            while (root) {
                el = root.getElementById(id);
                if (el)
                    return el;
                root = root.getRootNode().host?.getRootNode();
            }
        }
        catch {
            // Do nothing
        }
        return document.getElementById(id);
    }
    firstUpdated() {
        if (this.autofocus && !this.href) {
            setTimeout(() => this.focus(), 0);
        }
        if (this.commandfor) {
            this.commandForElement = this.closestWithId(this.commandfor);
        }
    }
    _handleButtonClick() {
        if (this.disabled)
            return;
        if (this.type === "submit") {
            this.internals.form.requestSubmit();
        }
        else if (this.type === "reset") {
            this.internals.form.reset();
        }
        if (this.command &&
            this.commandForElement &&
            "CommandEvent" in globalThis) {
            const commandEvent = new CommandEvent("command", {
                command: this.command,
            });
            this.commandForElement.dispatchEvent(commandEvent);
        }
    }
    resetFormControl() {
        this.value = this.#initialValue;
    }
    render() {
        const variant = this.variant || "secondary";
        return html ` ${this.href
            ? html `
						<w-link
							href=${this.href}
							target=${this.target}
							variant=${this.quiet ? "quiet" : variant}
							?small=${this.small}
							?loading=${this.loading}
							?autofocus=${this.autofocus}
							?full-width=${this.fullWidth}
							class=${this.buttonClass}
							rel=${this.target === "_blank" ? this.rel || "noopener" : undefined}
							aria-description=${ifDefined(this.#ariaDescription)}
						>
							<slot></slot>
						</w-link>
					`
            : html `
						<span part="base" class=${ifDefined(this.buttonClass)}>
							<slot></slot>
						</span>
					`}
		${this.loading
            ? html `<span
						class="sr-only"
						role="progressbar"
						aria-valuenow="0"
						aria-valuetext=${this.ariaValueTextLoading}
					></span>`
            : nothing}`;
    }
}
__decorate([
    property({ reflect: true, useDefault: true })
], WarpButton.prototype, "type", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "autofocus", void 0);
__decorate([
    property({ reflect: true, useDefault: true })
], WarpButton.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "quiet", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "icon-only" })
], WarpButton.prototype, "iconOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "small", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "loading", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "href", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "target", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpButton.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "rel", void 0);
__decorate([
    property({ attribute: "full-width", type: Boolean, reflect: true })
], WarpButton.prototype, "fullWidth", void 0);
__decorate([
    property({ attribute: "button-class", reflect: true })
], WarpButton.prototype, "buttonClass", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], WarpButton.prototype, "value", void 0);
__decorate([
    property()
], WarpButton.prototype, "commandfor", void 0);
__decorate([
    property()
], WarpButton.prototype, "commandForElement", void 0);
__decorate([
    property()
], WarpButton.prototype, "command", void 0);
if (!customElements.get("w-button")) {
    customElements.define("w-button", WarpButton);
}
export { WarpButton };
//# sourceMappingURL=button.js.map